import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactInfoCard = ({ icon: Icon, title, lines }) => (
  <div className="bg-white rounded-2xl p-6 border border-[#E7E5E4] hover:shadow-lg transition-shadow">
    <div className="flex items-start gap-4">
      <div className="w-12 h-12 rounded-xl bg-[#2D4A3E]/10 flex items-center justify-center flex-shrink-0">
        <Icon className="w-6 h-6 text-[#2D4A3E]" strokeWidth={1.5} />
      </div>
      <div>
        <h3 className="font-heading text-lg font-semibold text-[#1C1C1C] mb-2">{title}</h3>
        {lines.map((line, i) => (
          <p key={i} className="text-[#57534E]">{line}</p>
        ))}
      </div>
    </div>
  </div>
);

export const Contact = () => {
  const [searchParams] = useSearchParams();
  const subjectFromUrl = searchParams.get('subject') || '';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: subjectFromUrl,
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Update subject when URL changes
  useEffect(() => {
    if (subjectFromUrl) {
      setFormData(prev => ({ ...prev, subject: subjectFromUrl }));
    }
  }, [subjectFromUrl]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again or contact us via WhatsApp.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="min-h-screen bg-[#FAF9F6] pt-24">
      {/* Hero Section */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-[#F2F0E9] to-[#FAF9F6]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-sm font-semibold tracking-widest uppercase text-[#C69C6D] mb-4 block">
            Get in Touch
          </span>
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-[#1C1C1C] mb-6">
            We'd Love to<br />Hear From You
          </h1>
          <p className="text-lg text-[#57534E] max-w-2xl mx-auto">
            Have questions about our products or need guidance on Ayurvedic remedies? 
            Reach out to us and our team will be happy to assist.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-7">
              <div className="bg-white rounded-3xl p-8 md:p-12 shadow-lg border border-[#E7E5E4]">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-[#1C1C1C] mb-2">
                  Send us a Message
                </h2>
                <p className="text-[#57534E] mb-8">
                  Fill out the form below and we'll respond within 24 hours.
                </p>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-[#1C1C1C] font-medium">
                        Full Name *
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="contact-name-input"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Your name"
                        className="bg-[#FAF9F6] border-[#E7E5E4] focus:border-[#2D4A3E] focus:ring-1 focus:ring-[#2D4A3E] rounded-xl px-4 py-3 h-12"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="email" className="text-[#1C1C1C] font-medium">
                        Email Address *
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="contact-email-input"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="your@email.com"
                        className="bg-[#FAF9F6] border-[#E7E5E4] focus:border-[#2D4A3E] focus:ring-1 focus:ring-[#2D4A3E] rounded-xl px-4 py-3 h-12"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="text-[#1C1C1C] font-medium">
                        Phone Number
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        data-testid="contact-phone-input"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+91 98470 23246"
                        className="bg-[#FAF9F6] border-[#E7E5E4] focus:border-[#2D4A3E] focus:ring-1 focus:ring-[#2D4A3E] rounded-xl px-4 py-3 h-12"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="subject" className="text-[#1C1C1C] font-medium">
                        Subject
                      </Label>
                      <Input
                        id="subject"
                        name="subject"
                        data-testid="contact-subject-input"
                        value={formData.subject}
                        onChange={handleChange}
                        placeholder="Product enquiry, General query, etc."
                        className="bg-[#FAF9F6] border-[#E7E5E4] focus:border-[#2D4A3E] focus:ring-1 focus:ring-[#2D4A3E] rounded-xl px-4 py-3 h-12"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[#1C1C1C] font-medium">
                      Your Message *
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="contact-message-input"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      placeholder="Tell us how we can help you..."
                      rows={5}
                      className="bg-[#FAF9F6] border-[#E7E5E4] focus:border-[#2D4A3E] focus:ring-1 focus:ring-[#2D4A3E] rounded-xl px-4 py-3 resize-none"
                    />
                  </div>
                  
                  <Button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={isSubmitting}
                    className="w-full bg-[#2D4A3E] text-white hover:bg-[#1F352B] rounded-full px-8 py-6 text-lg transition-all duration-300 shadow-lg hover:shadow-[#2D4A3E]/20 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                    {!isSubmitting && <Send className="ml-2 w-5 h-5" />}
                  </Button>
                </form>
              </div>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-5 space-y-6">
              <ContactInfoCard 
                icon={MapPin} 
                title="Visit Us" 
                lines={["Amjatha Pharma", "Irimbiliyam, Valanchery", "Kerala, India - 679572"]} 
              />
              <ContactInfoCard 
                icon={Phone} 
                title="Call Us" 
                lines={["+91 98470 23246"]} 
              />
              <ContactInfoCard 
                icon={Mail} 
                title="Email Us" 
                lines={["amjathapharma@gmail.com"]} 
              />
              <ContactInfoCard 
                icon={Clock} 
                title="Working Hours" 
                lines={["Monday - Saturday", "9:00 AM - 6:00 PM", "Sunday: Closed"]} 
              />

              {/* WhatsApp CTA */}
              <div className="bg-[#25D366] rounded-2xl p-8 text-white">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                    <MessageCircle className="w-7 h-7" strokeWidth={1.5} fill="white" />
                  </div>
                  <div>
                    <h3 className="font-heading text-xl font-semibold">Quick Response?</h3>
                    <p className="text-white/80 text-sm">Chat with us on WhatsApp</p>
                  </div>
                </div>
                <a 
                  href="https://wa.me/919847023246?text=Hello! I have a query about Amjatha Pharma products."
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-btn"
                  className="block"
                >
                  <Button className="w-full bg-white text-[#25D366] hover:bg-white/90 rounded-full py-6 text-lg font-semibold transition-all hover:-translate-y-0.5">
                    Chat Now
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section (Placeholder) */}
      <section className="py-16 bg-[#F2F0E9]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-[#2D4A3E]/10 rounded-3xl h-80 flex items-center justify-center">
            <div className="text-center">
              <MapPin className="w-12 h-12 text-[#2D4A3E] mx-auto mb-4" strokeWidth={1.5} />
              <p className="text-[#57534E] font-medium">
                Amjatha Pharma<br />
                Irimbiliyam, Valanchery<br />
                Kerala, India - 679572
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
